import type { OverviewSnapshot } from '@kenocyber/game-domain';
import type { EconomyMeResponse } from '@kenocyber/shared';
import { useState } from 'react';
import { BleedButtonLabel } from '../game/bleed-button-label';
import { formatDecimal, formatNumber, formatVipRemaining } from './format';

interface HudPanelProps {
  readonly snapshot: OverviewSnapshot | null;
  /** The SERVER's wallets and VIP state. Null until the first read lands. */
  readonly economy: EconomyMeResponse | null;
  readonly onAllocate: (allocation: { damage: bigint; health: bigint; speed: bigint }) => void;
  /**
   * Manda rebentar as marcas de sangramento (gate V28G.N).
   *
   * Opcional, como as afordâncias do duelo são: o HUD também é desenhado em
   * estados onde não há autoridade a quem dar ordens, e um botão que não fizesse
   * nada seria pior do que a sua ausência.
   */
  readonly onDetonateBleed?: () => void;
}

/**
 * Pending spend, in bigint.
 *
 * A point balance has no cap, so the arithmetic that decides what the player
 * may still spend is done in bigint and only the display is formatted.
 */
interface PendingAllocation {
  damage: bigint;
  health: bigint;
  speed: bigint;
}

const EMPTY_ALLOCATION: PendingAllocation = { damage: 0n, health: 0n, speed: 0n };

const ALLOCATION_ROWS = [
  { key: 'damage' as const, label: 'Dano', effect: '+1' },
  { key: 'health' as const, label: 'Vida', effect: '+10' },
  { key: 'speed' as const, label: 'Vel. ataque', effect: '+0,02/s' },
];

/**
 * Compact HUD floating over the top-left of the arena.
 *
 * It carries only what a player needs at a glance while hunting - level, gold,
 * health, XP and the free-point button. Everything analytical lives in the
 * Overview panel instead, so this stays small and never covers much of the
 * playfield.
 */
export function HudPanel({ snapshot, economy, onAllocate, onDetonateBleed }: HudPanelProps) {
  const [pending, setPending] = useState<PendingAllocation>(EMPTY_ALLOCATION);
  const [open, setOpen] = useState(false);

  if (!snapshot) {
    return (
      <div className="hud hud-left" data-testid="hud-left">
        <span className="hud-loading">Iniciando caçada…</span>
      </div>
    );
  }

  const freePoints = BigInt(snapshot.freePoints);
  const pendingTotal = pending.damage + pending.health + pending.speed;
  const pointsLeft = freePoints - pendingTotal;
  const healthPercent = Math.max(0, Math.min(100, (snapshot.health / snapshot.maxHealth) * 100));
  // The ratio is computed once in bigint by the simulation, so the bar cannot
  // become NaN however large the level gets.
  const xpPercent = Math.max(0, Math.min(100, snapshot.xpProgressBps / 100));

  const bump = (key: keyof PendingAllocation, delta: bigint) => {
    setPending((current) => {
      const next = current[key] + delta;
      if (next < 0n) {
        return current;
      }
      const total = current.damage + current.health + current.speed - current[key] + next;
      // Never allow more than the available balance.
      return total > freePoints ? current : { ...current, [key]: next };
    });
  };

  const confirm = () => {
    if (pendingTotal === 0n) {
      return;
    }
    onAllocate(pending);
    setPending(EMPTY_ALLOCATION);
    setOpen(false);
  };

  const cancel = () => {
    setPending(EMPTY_ALLOCATION);
    setOpen(false);
  };

  return (
    <div className="hud hud-left" data-testid="hud-left">
      <div className="hud-row hud-top-row">
        <span className="hud-chip" data-testid="hud-level">
          <span className="hud-chip-label">LVL</span>
          <span className="hud-chip-value" data-testid="hud-level-value">
            {formatDecimal(snapshot.level)}
          </span>
        </span>
        <span className="hud-chip hud-chip-gold" data-testid="hud-gold">
          <span className="hud-chip-label">GOLD</span>
          <span className="hud-chip-value" data-testid="hud-gold-value">
            {formatDecimal(snapshot.gold)}
          </span>
        </span>
        {/* Cash beside Gold, and never predicted: the arena's simulation earns
            Gold, so the HUD can show it a frame early, but Cash only ever moves
            through the ledger. An em dash until the server has answered is the
            honest state - a zero would be a claim nobody made. */}
        <span className="hud-chip hud-chip-cash" data-testid="hud-cash">
          <span className="hud-chip-label">CASH</span>
          <span className="hud-chip-value" data-testid="hud-cash-value">
            {economy ? formatDecimal(economy.cash) : '—'}
          </span>
        </span>
      </div>

      {/* Derived by the server from its own clock on every read, so an
          entitlement stops being advertised the moment it is over. */}
      {economy ? (
        <div
          className={`hud-vip${economy.vip.active ? ' is-active' : ''}`}
          data-testid="hud-vip"
          data-vip-active={economy.vip.active ? 'true' : 'false'}
        >
          {economy.vip.active ? (
            <>
              <span className="hud-vip-badge">VIP ATIVO</span>
              <span className="hud-vip-remaining" data-testid="hud-vip-remaining">
                {formatVipRemaining(economy.vip.remainingSeconds)}
              </span>
            </>
          ) : (
            <span className="hud-vip-badge hud-vip-inactive">SEM VIP</span>
          )}
        </div>
      ) : null}

      <div className="hud-bar-block" data-testid="hud-health">
        <div className="hud-bar hud-bar-health">
          <div className="hud-bar-fill" style={{ width: `${healthPercent}%` }} />
        </div>
        <span className="hud-bar-caption">
          {formatNumber(snapshot.health, 0)}/{formatNumber(snapshot.maxHealth, 0)}
        </span>
      </div>

      <div className="hud-bar-block" data-testid="hud-xp">
        <div className="hud-bar hud-bar-xp">
          <div className="hud-bar-fill" style={{ width: `${xpPercent}%` }} />
        </div>
        <span className="hud-bar-caption" data-testid="hud-xp-value">
          XP {formatDecimal(snapshot.xpIntoLevel)}/{formatDecimal(snapshot.xpForNextLevel)}
        </span>
      </div>

      <button
        type="button"
        className={`hud-points-button${freePoints > 0n ? ' has-points' : ''}`}
        data-testid="hud-allocate-open"
        disabled={freePoints === 0n}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        Distribuir pontos ({formatDecimal(snapshot.freePoints)})
      </button>

      {/*
       * **DETONAR** (gate V28G.N).
       *
       * Um botão próprio, ao lado do de pontos e dentro do HUD que já flutua
       * sobre a arena - portanto abri-lo não redimensiona nem recria o canvas.
       *
       * Não dispara o canhão e não substitui controlo nenhum. Tem tecla - o `1`,
       * pedido pelo dono depois do primeiro ensaio - e ela estava LIVRE: o §4
       * proíbe roubar uma já ocupada, e nenhum controlo desta arena usava os
       * algarismos.
       *
       * O total vem do estado AUTORITATIVO da simulação, e é o mesmo número que
       * a ordem vai rebentar. Desativado sem marcas e com a nave abatida.
       */}
      {onDetonateBleed ? (
        <button
          type="button"
          className="hud-detonate-button"
          data-testid="hud-detonate"
          disabled={snapshot.bleedingMarks === 0 || snapshot.phase === 'dead'}
          onClick={onDetonateBleed}
        >
          <BleedButtonLabel marks={snapshot.bleedingMarks} />
        </button>
      ) : null}

      {open && freePoints > 0n ? (
        <div className="hud-allocate" data-testid="hud-allocate-panel">
          {ALLOCATION_ROWS.map((row) => (
            <div className="hud-allocate-row" key={row.key}>
              <span className="hud-allocate-label">
                {row.label} <em>{row.effect}</em>
              </span>
              <div className="hud-allocate-controls">
                <button
                  type="button"
                  data-testid={`alloc-${row.key}-minus`}
                  onClick={() => bump(row.key, -1n)}
                  disabled={pending[row.key] === 0n}
                >
                  −
                </button>
                <span data-testid={`alloc-${row.key}-value`}>
                  {formatDecimal(pending[row.key].toString(10))}
                </span>
                <button
                  type="button"
                  data-testid={`alloc-${row.key}-plus`}
                  onClick={() => bump(row.key, 1n)}
                  disabled={pointsLeft === 0n}
                >
                  +
                </button>
              </div>
            </div>
          ))}
          <div className="hud-allocate-footer">
            <span data-testid="alloc-remaining">
              Restam: {formatDecimal(pointsLeft.toString(10))}
            </span>
            <div className="hud-allocate-actions">
              <button
                type="button"
                className="ghost-button"
                data-testid="alloc-cancel"
                onClick={cancel}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="neon-button"
                data-testid="alloc-confirm"
                disabled={pendingTotal === 0n}
                onClick={confirm}
              >
                Confirmar
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
